import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { 
  Truck, 
  Package, 
  RefreshCw, 
  Calendar,
  Clock
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const REFRESH_INTERVAL = 30000;

export default function RTODashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState("today");

  const getDateOptions = () => {
    const today = new Date();
    const options = [{ label: "Today", value: "today", date: today }];
    for (let i = 1; i <= 3; i++) {
      const date = new Date();
      date.setDate(today.getDate() - i);
      options.push({
        label: date.toLocaleDateString("en-IN", { day: "numeric", month: "short" }),
        value: date.toISOString().split("T")[0],
        date: date,
      });
    }
    return options;
  };

  const dateOptions = getDateOptions();

  const fetchReturns = useCallback(async (date = selectedDate, showLoader = false) => {
    try {
      if (showLoader) setRefreshing(true);
      const dateParam = date === "today" ? "" : `?date=${date}`;
      const response = await axios.get(`${API}/rto/daily${dateParam}`, { timeout: 120000 });
      setData(response.data);
      setLastUpdated(new Date());
      setLoading(false);
      setRefreshing(false);

      // Auto-sync RTO items to Operations Inventory (dedup handled server-side)
      const orders = response.data?.orders || [];
      if (orders.length > 0) {
        try {
          const rtoItems = orders.map(o => ({
            order_id: o.order_id,
            item: o.item,
            quantity: o.items_count || 1,
          }));
          await axios.post(`${API}/ops-inventory/process-rto`, { items: rtoItems });
        } catch (syncErr) {
          console.warn("RTO ops-inventory sync skipped:", syncErr.message);
        }
      }
    } catch (e) {
      console.error("Error fetching RTO returns:", e);
      toast.error("Failed to fetch RTO data");
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedDate]);

  const handleManualRefresh = () => {
    setRefreshing(true);
    fetchReturns(selectedDate, true);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setRefreshing(true);
    fetchReturns(date, true);
  };

  useEffect(() => {
    fetchReturns();
    let refreshInterval;
    if (selectedDate === "today") {
      refreshInterval = setInterval(() => fetchReturns(), REFRESH_INTERVAL);
    }
    const timeInterval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => {
      if (refreshInterval) clearInterval(refreshInterval);
      clearInterval(timeInterval);
    };
  }, [selectedDate, fetchReturns]);

  const getTimeSinceUpdate = () => {
    const seconds = Math.floor((currentTime - lastUpdated) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    return `${Math.floor(seconds / 60)}m ago`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-600" />
        <span className="ml-3 text-lg text-slate-600">Loading RTO data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="rto-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 font-heading">RTO Returns</h2>
          <p className="text-slate-600 mt-1">Track Return-to-Origin shipments from Shiprocket</p>
        </div>
      </div>

      {/* Date tabs and refresh */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div className="flex gap-2 flex-wrap">
          {dateOptions.map((option) => (
            <Button
              key={option.value}
              variant={selectedDate === option.value ? "default" : "outline"}
              onClick={() => handleDateChange(option.value)}
              data-testid={`rto-tab-${option.value}`}
            >
              {option.value === "today" && <Calendar className="w-4 h-4 mr-2" />}
              {option.label}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-4">
          <Button
            onClick={handleManualRefresh}
            disabled={refreshing}
            data-testid="rto-refresh-button"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          {selectedDate === "today" && !refreshing && (
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
              <span className="text-sm text-slate-500">Updated {getTimeSinceUpdate()}</span>
            </div>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Total Count Card */}
        <Card className="card-hover relative overflow-hidden" data-testid="rto-total-card">
          {refreshing && (
            <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10 rounded-xl">
              <RefreshCw className="w-8 h-8 animate-spin text-slate-600" />
            </div>
          )}
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Inbound Returns</CardTitle>
            <Package className="w-4 h-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-5xl font-bold text-slate-900 font-mono" data-testid="rto-total-count">
              {data?.total_count || 0}
            </div>
            <p className="text-sm text-slate-500 mt-2">
              {selectedDate === "today" ? "Today" : data?.date}
            </p>
          </CardContent>
        </Card>

        {/* Courier Breakdown */}
        <Card className="lg:col-span-2 card-hover">
          <CardHeader>
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Truck className="w-5 h-5 text-slate-500" />
              Courier Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {data?.courier_tally?.length > 0 ? (
                data.courier_tally.map((courier, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center gap-2">
                      <Truck className="w-4 h-4" style={{ color: courier.color }} />
                      <span className="text-sm font-medium text-slate-700">{courier.courier_name}</span>
                    </div>
                    <span 
                      className="text-xl font-bold font-mono" 
                      style={{ color: courier.color }}
                    >
                      {courier.count}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-slate-500 col-span-full">No courier data available</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Orders Table */}
      <Card className="relative" data-testid="rto-orders-table">
        {refreshing && (
          <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10 rounded-lg">
            <RefreshCw className="w-8 h-8 animate-spin text-slate-600" />
          </div>
        )}
        <CardHeader>
          <CardTitle className="text-lg font-heading">
            RTO Orders ({data?.total_count || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 font-semibold text-slate-600">Order ID</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-600">Courier</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-600">Item</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {data?.orders?.length > 0 ? (
                  data.orders.map((order, idx) => (
                    <tr 
                      key={idx} 
                      className="border-b border-slate-100 hover:bg-slate-50"
                      data-testid={`rto-order-${order.order_id}`}
                    >
                      <td className="py-3 px-4 font-mono font-medium text-slate-700">
                        {order.order_id}
                      </td>
                      <td className="py-3 px-4">
                        <span className="flex items-center gap-2 text-slate-700">
                          <Truck className="w-4 h-4 text-slate-400" />
                          {order.courier}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="flex items-center gap-2 text-slate-700">
                          <Package className="w-4 h-4 text-slate-400" />
                          {order.item}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                          {order.status}
                        </Badge>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="py-12 text-center">
                      <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-lg text-slate-500">
                        No returns for {selectedDate === "today" ? "today" : data?.date}
                      </p>
                      <p className="text-sm text-slate-400 mt-1">
                        Check back later or try a different date
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Last updated info */}
      {data?.last_updated && (
        <div className="text-center text-sm text-slate-400">
          <Clock className="w-4 h-4 inline mr-1" />
          Data last updated: {new Date(data.last_updated).toLocaleString()}
        </div>
      )}
    </div>
  );
}
